"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SearchInput } from "@/components/ui/search-input";
import { readSafeReturnTo } from "@/lib/return-navigation";
import { cn } from "@/lib/utils";
import { ArrowLeftIcon } from "lucide-react";
import { useRouter } from "next/navigation";
import type { ReactNode } from "react";

type PageHeaderProps = {
  title: ReactNode;
  description?: ReactNode;
  onBack?: () => void;
  backLabel?: string;
  /** Renders the Back control. Top-level pages navigate via the sidebar, so this
   *  is only for nested views (drill-downs) that have no other way out. */
  showBack?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  searchAriaLabel?: string;
  actions?: ReactNode;
  className?: string;
  variant?: "default" | "collection";
};

export function PageHeader({
  title,
  description,
  onBack,
  backLabel = "Back",
  showBack = false,
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search...",
  searchAriaLabel,
  actions,
  className,
  variant = "default",
}: PageHeaderProps) {
  const router = useRouter();
  const showSearch = typeof searchValue === "string" && Boolean(onSearchChange);
  const showControls = showSearch || Boolean(actions);
  const isCollection = variant === "collection";
  const handleBack = () => {
    const returnTo = typeof window === "undefined"
      ? null
      : readSafeReturnTo(window.location.search);
    if (returnTo) {
      router.push(returnTo);
      return;
    }
    onBack?.();
  };

  return (
    <div className={cn("shrink-0", className)}>
      {showBack && onBack ? (
        <div className="hidden px-4 pt-4 pb-1 sm:block">
          <Button
            className="gap-2 text-muted-foreground"
            onClick={handleBack}
            size="sm"
            type="button"
            variant="ghost"
          >
            <ArrowLeftIcon className="size-4" />
            {backLabel}
          </Button>
        </div>
      ) : null}

      <div className={cn(
        isCollection
          ? "mx-auto w-full max-w-4xl pb-3 pl-14 pr-4 pt-8 sm:px-10 sm:pt-12"
          : "border-b border-border py-2.5 pl-14 pr-4 sm:px-5 sm:py-3.5"
      )}>
        <div
          className={cn(
            "flex gap-2",
            isCollection
              ? "flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
              : showSearch
              ? "flex-col lg:flex-row lg:items-end lg:justify-between"
              : "flex-row items-center justify-between"
          )}
        >
          <div className="min-w-0 flex-1 space-y-0.5">
            <h1
              className={cn(
                isCollection
                  ? "text-xl font-semibold tracking-tight text-foreground sm:text-2xl"
                  : "text-base font-semibold tracking-tight text-foreground sm:text-lg",
                typeof title === "string" && "truncate"
              )}
            >
              {title}
            </h1>
            {description ? (
              <div className="hidden max-w-2xl text-xs text-muted-foreground sm:block">
                {description}
              </div>
            ) : null}
          </div>

          {showControls ? (
            <div
              className={cn(
                "flex min-w-0 flex-row flex-wrap items-center gap-2 lg:justify-end",
                isCollection
                  ? "w-full flex-nowrap sm:w-auto"
                  : showSearch ? "w-full max-w-xl flex-nowrap" : "shrink-0 justify-end"
              )}
            >
              {showSearch && isCollection ? (
                <SearchInput
                  className="min-w-0 flex-1 sm:w-60 sm:flex-none"
                  onChange={onSearchChange!}
                  placeholder={searchPlaceholder}
                  ariaLabel={searchAriaLabel ?? searchPlaceholder}
                  compact
                  value={searchValue!}
                />
              ) : showSearch ? (
                <Input
                  aria-label={searchAriaLabel ?? searchPlaceholder}
                  className="h-8 flex-1"
                  onChange={(event) => onSearchChange?.(event.target.value)}
                  placeholder={searchPlaceholder}
                  value={searchValue}
                />
              ) : null}
              {actions}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
